import { sharedRefreshCoordinator } from '@quickbee/auth/internal';
import { computeBackoffDelay, generateCorrelationId } from '@quickbee/utils';
import { getContextDescriptor, getApiMode, resolveContextBaseUrl, getDevSession, isAuthDisabled, getActiveApp } from '@quickbee/api-config';
export { UnreachableContextError } from '@quickbee/api-config';
import { useAuthStore } from '@quickbee/auth';

// src/factory/createApiClient.ts

// src/download/fileDownload.ts
function parseContentDispositionFileName(header) {
  if (!header) return null;
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended?.[2]) {
    try {
      return decodeURIComponent(extended[2].trim());
    } catch {
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  return bare?.[1]?.trim() || null;
}
function fileNameFromUrl(url) {
  const [path] = url.split("?");
  const segments = (path ?? "").split("/").filter(Boolean);
  const last = segments[segments.length - 1];
  return last ? decodeURIComponent(last) : "download";
}
async function toFileDownload(response, url) {
  const blob = await response.blob();
  return {
    blob,
    fileName: parseContentDispositionFileName(response.headers.get("content-disposition")) ?? fileNameFromUrl(url),
    contentType: response.headers.get("content-type") ?? blob.type ?? "application/octet-stream"
  };
}
function saveFileDownload(download) {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("[api-client] saveFileDownload requires a browser document.");
  }
  const objectUrl = URL.createObjectURL(download.blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = download.fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

// src/envelope/unwrapEnvelope.ts
function isSuccessEnvelope(body) {
  return typeof body === "object" && body !== null && "data" in body;
}
function isPagedEnvelope(body) {
  if (typeof body !== "object" || body === null) return false;
  const candidate = body;
  return Array.isArray(candidate.data) && typeof candidate.pagination === "object" && candidate.pagination !== null;
}
function isErrorEnvelope(body) {
  return typeof body === "object" && body !== null && "errors" in body && Array.isArray(body.errors);
}
function isPagedResult(value) {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value;
  return Array.isArray(candidate.items) && typeof candidate.pagination === "object" && candidate.pagination !== null;
}
function normalisePagination(pagination) {
  const total = pagination?.total;
  return {
    cursor: pagination?.cursor ?? null,
    hasMore: pagination?.hasMore ?? false,
    total: total === void 0 || total === null || total < 0 ? null : total
  };
}
function unwrapEnvelope(body) {
  if (isPagedEnvelope(body)) {
    return {
      items: body.data,
      pagination: normalisePagination(body.pagination)
    };
  }
  if (isSuccessEnvelope(body)) {
    return body.data;
  }
  return body;
}
function readResponseMeta(body) {
  if (typeof body !== "object" || body === null) return void 0;
  const meta = body.meta;
  if (typeof meta !== "object" || meta === null) return void 0;
  return meta;
}

// src/errors/errorCodeMap.ts
var errorCodeMap = {
  // ─── Validation / Bad Request (400) ────────────────────────────────────
  VALIDATION_400_001: { category: "validation", retryable: false },
  VALIDATION_400_002: { category: "validation", retryable: false },
  VALIDATION_400_003: { category: "validation", retryable: false },
  MISSING_TENANT_400_001: { category: "validation", retryable: false },
  IDEMPOTENCY_KEY_MISSING_400_001: { category: "validation", retryable: false },
  // ─── Authentication (401) ──────────────────────────────────────────────
  AUTH_REQUIRED_401_001: { category: "auth", retryable: false },
  AUTH_TOKEN_INVALID_401_002: { category: "auth", retryable: false },
  SESSION_INVALIDATED_401_003: { category: "auth", retryable: false },
  // ─── Authorization (403) ───────────────────────────────────────────────
  AUTHZ_FORBIDDEN_403_001: { category: "authorization", retryable: false },
  TENANT_ACCESS_DENIED_403_001: { category: "authorization", retryable: false },
  // ─── Not Found (404) ────────────────────────────────────────────────────
  NOT_FOUND_404_001: { category: "not_found", retryable: false },
  // ─── Conflict (409) ─────────────────────────────────────────────────────
  OPTIMISTIC_LOCK_CONFLICT_409_001: { category: "conflict", retryable: false },
  SESSION_CONFLICT_409_002: { category: "conflict", retryable: false },
  IDEMPOTENT_REQUEST_IN_PROGRESS_409_003: { category: "conflict", retryable: true },
  // ─── Unprocessable Entity (422) — generic ─────────────────────────────
  BUSINESS_RULE_VIOLATION_422_001: { category: "business", retryable: false },
  RESOURCE_EXHAUSTED_422_002: { category: "business", retryable: false },
  // ─── QuickBee Finance-Specific (422) ────────────────────────────────────
  GOLD_BALANCE_INSUFFICIENT: { category: "business", retryable: false },
  KYC_PENDING: { category: "business", retryable: false },
  SCHEME_LOCKED: { category: "business", retryable: false },
  CERTIFICATE_EXPIRED: { category: "business", retryable: false },
  PRICE_STALE: { category: "business", retryable: false },
  AML_FLAGGED: { category: "business", retryable: false },
  // ─── Rate Limiting (429) ───────────────────────────────────────────────
  RATE_LIMIT_EXCEEDED_429_001: { category: "rate_limit", retryable: true },
  // ─── Message Processing ────────────────────────────────────────────────
  MESSAGE_PROCESSING_FAILED: { category: "server", retryable: false },
  MESSAGE_PROCESSING_TRANSIENT_FAILURE: { category: "server", retryable: true },
  // ─── Internal Server Error (500) / Service Unavailable (503) ──────────
  INTERNAL_SERVER_ERROR_500_001: { category: "server", retryable: false },
  SERVICE_UNAVAILABLE_503_001: { category: "server", retryable: true },
  // ══ Service-specific ErrorCatalog codes ═══════════════════════════════════
  // Each service registers its own namespaced codes with the platform
  // ErrorRegistry. They are business-rule violations unless their name says
  // otherwise (a `*_NOT_FOUND` is a not_found, an `ALREADY_*`/`*_EXISTS` is a
  // conflict, an `INVALID_*`/`*_MISMATCH` is a validation error), because that
  // is what decides the UI treatment.
  // ─── goals-service ─────────────────────────────────────────────
  GOAL_LIMIT_EXCEEDED: { category: "business", retryable: false },
  GOAL_NOT_ACTIVE: { category: "business", retryable: false },
  // ─── ledger-service ────────────────────────────────────────────
  ACCOUNT_NOT_ACTIVE: { category: "business", retryable: false },
  DUPLICATE_SINGLETON_ACCOUNT: { category: "conflict", retryable: false },
  INSUFFICIENT_LEDGER_BALANCE: { category: "business", retryable: false },
  INVALID_RECONCILIATION_STATE: { category: "validation", retryable: false },
  SUSPENSE_CASE_ALREADY_RESOLVED: { category: "conflict", retryable: false },
  // ─── loan-service ──────────────────────────────────────────────
  LOAN_ATTACHMENTS_NOT_CONFIGURED: { category: "business", retryable: false },
  LOAN_ATTACHMENT_INVALID: { category: "validation", retryable: false },
  LOAN_ATTACHMENT_INVALID_STATE: { category: "business", retryable: false },
  LOAN_ATTACHMENT_NOT_UPLOADED: { category: "business", retryable: false },
  LOAN_COMMENT_INVALID: { category: "validation", retryable: false },
  LOAN_LEAD_AMOUNT_TOO_LOW: { category: "business", retryable: false },
  LOAN_LEAD_INVALID_AMOUNT: { category: "business", retryable: false },
  LOAN_LEAD_INVALID_STATE: { category: "business", retryable: false },
  LOAN_PARTNER_BANK_INVALID: { category: "validation", retryable: false },
  LOAN_REDEMPTION_NOT_FOUND: { category: "not_found", retryable: false },
  // ─── network-service ───────────────────────────────────────────
  COUPON_NOT_REDEEMABLE: { category: "business", retryable: false },
  NETWORK_INVALID_STATE: { category: "business", retryable: false },
  NETWORK_PARTICIPATION_INACTIVE: { category: "business", retryable: false },
  SETTLEMENT_INVALID_STATE: { category: "business", retryable: false },
  // ─── redemption-service ────────────────────────────────────────
  REDEMPTION_ALREADY_TERMINAL: { category: "conflict", retryable: false },
  REDEMPTION_INVALID_STATE: { category: "business", retryable: false },
  // ─── scheme-service ────────────────────────────────────────────
  CONTRIBUTION_MODE_MISMATCH: { category: "validation", retryable: false },
  ENROLLMENT_LIMIT_REACHED: { category: "business", retryable: false },
  ENROLLMENT_NOT_ACTIVE: { category: "business", retryable: false },
  SCHEME_FINANCIAL_TERMS_LOCKED: { category: "business", retryable: false },
  SCHEME_NOT_ACTIVE: { category: "business", retryable: false },
  // ─── comms-service ─────────────────────────────────────────────
  NO_PROVIDER_CONFIGURED: { category: "business", retryable: false },
  TEMPLATE_NOT_ACTIVE_ON_CHANNEL: { category: "business", retryable: false },
  WHATSAPP_TEMPLATE_NOT_APPROVED: { category: "business", retryable: false },
  // ─── helpdesk-service ──────────────────────────────────────────
  TICKET_ALREADY_RATED: { category: "conflict", retryable: false },
  TICKET_ESCALATION_INVALID: { category: "validation", retryable: false },
  TICKET_INVALID_STATE_TRANSITION: { category: "business", retryable: false },
  TICKET_NOT_ACTIVE: { category: "business", retryable: false },
  // Help content (FAQ + knowledge base). The key clash is a `conflict` because
  // the author's fix is to pick another key; the state transition mirrors the
  // ticket one — a refusal the author has to resolve, not retry.
  ARTICLE_COLLECTION_KEY_TAKEN: { category: "conflict", retryable: false },
  ARTICLE_INVALID_STATE_TRANSITION: { category: "business", retryable: false },
  // ─── compliance-service ────────────────────────────────────────
  RULE_NOT_ACTIVE: { category: "business", retryable: false },
  TASK_NOT_ACTIONABLE: { category: "business", retryable: false },
  // ─── dispute-service ───────────────────────────────────────────
  DISPUTE_NOT_ACTIONABLE: { category: "business", retryable: false },
  // ─── identity-service ──────────────────────────────────────────
  DUPLICATE_ACCOUNT: { category: "conflict", retryable: false },
  KYC_INVALID_TRANSITION: { category: "business", retryable: false },
  KYC_NOT_PERMITTED: { category: "authorization", retryable: false },
  OTP_INVALID: { category: "validation", retryable: false },
  OTP_LOCKED: { category: "business", retryable: false },
  PASSWORD_INVALID: { category: "validation", retryable: false },
  // ─── lender-service ────────────────────────────────────────────
  LENDER_CODE_TAKEN: { category: "business", retryable: false },
  LENDER_INVALID_PROFILE: { category: "business", retryable: false },
  LENDER_INVALID_STATE: { category: "business", retryable: false },
  LENDER_NOT_ACTIVE: { category: "business", retryable: false },
  LENDER_NOT_FOUND: { category: "not_found", retryable: false },
  LENDER_USER_ACCOUNT_CONFLICT: { category: "conflict", retryable: false },
  LENDER_USER_EMAIL_TAKEN: { category: "business", retryable: false },
  LENDER_USER_INVALID_STATE: { category: "business", retryable: false },
  LENDER_USER_NOT_ASSIGNABLE: { category: "business", retryable: false },
  LENDER_USER_NOT_FOUND: { category: "not_found", retryable: false },
  LENDER_USER_PROVISIONING_FAILED: { category: "business", retryable: false },
  LENDER_USER_WRONG_LENDER: { category: "business", retryable: false },
  // ─── partner-service ───────────────────────────────────────────
  COMMISSION_PLAN_ENTRY_EXISTS: { category: "conflict", retryable: false },
  COMMISSION_RATE_INVALID: { category: "validation", retryable: false },
  COMMISSION_RATE_NOT_CONFIGURED: { category: "business", retryable: false },
  PARTNER_AGENT_ACCOUNT_CONFLICT: { category: "conflict", retryable: false },
  PARTNER_AGENT_EMAIL_TAKEN: { category: "business", retryable: false },
  PARTNER_AGENT_INVALID_STATE: { category: "business", retryable: false },
  PARTNER_AGENT_PROVISIONING_FAILED: { category: "business", retryable: false },
  PARTNER_CODE_TAKEN: { category: "business", retryable: false },
  PARTNER_INVALID_STATE: { category: "business", retryable: false },
  PAYOUT_ALREADY_GENERATED: { category: "conflict", retryable: false },
  PAYOUT_INVALID_STATE: { category: "business", retryable: false },
  REFERRAL_AGENT_NOT_ACTIVE: { category: "business", retryable: false },
  REFERRAL_ALREADY_SETTLED: { category: "conflict", retryable: false },
  REFERRAL_DISABLED: { category: "business", retryable: false },
  REFERRAL_INVALID_STATE: { category: "business", retryable: false },
  REFERRAL_PHONE_ALREADY_CLAIMED: { category: "conflict", retryable: false },
  REFERRAL_PHONE_INVALID: { category: "validation", retryable: false },
  // ─── pricing-service ───────────────────────────────────────────
  PRICING_NO_ACTIVE_OVERRIDE: { category: "business", retryable: false },
  // ─── reporting-service ─────────────────────────────────────────
  REPORT_EXPIRED: { category: "business", retryable: false },
  REPORT_FAILED: { category: "business", retryable: false },
  REPORT_NOT_READY: { category: "business", retryable: false },
  REPORT_STATE_INVALID: { category: "validation", retryable: false },
  // ─── tenant-service ────────────────────────────────────────────
  BRANCH_HAS_ACTIVE_STAFF: { category: "business", retryable: false },
  INVALID_TENANT_TRANSITION: { category: "validation", retryable: false },
  LAST_ACTIVE_ADMIN: { category: "business", retryable: false },
  SCHEMA_PROVISIONING_FAILED: { category: "business", retryable: false }
};
var UNKNOWN_ERROR_META = { category: "unknown", retryable: false };
function getErrorCodeMeta(code) {
  return errorCodeMap[code] ?? UNKNOWN_ERROR_META;
}

// src/errors/ApiError.ts
var ApiError = class _ApiError extends Error {
  /** HTTP status the error travelled on. `0` for a network failure that never reached the server. */
  status;
  /** Machine-readable code — mirrors a `quickbee-sdk-core` `ErrorCode` name, or a service-specific `ErrorCatalog` code (§4.3). */
  code;
  /** Dot-notation field path for field-level validation errors; `null` for non-field errors. */
  field;
  /** Any additional error entries beyond the first, when the envelope carried more than one (e.g. multi-field validation). */
  details;
  /** `meta.requestId` from the response envelope, for support-ticket correlation. */
  requestId;
  /** The full, unmodified `errors[]` array from the response envelope. */
  errors;
  constructor(options) {
    super(options.message, options.cause !== void 0 ? { cause: options.cause } : void 0);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.field = options.field ?? null;
    this.details = options.details;
    this.requestId = options.requestId;
    this.errors = options.errors ?? [];
  }
  /** Category derived from `errorCodeMap.ts`, kept in lockstep with the backend `ErrorCode` catalogue (§4.3). */
  get category() {
    return getErrorCodeMeta(this.code).category;
  }
  /**
   * Whether `errorCodeMap.ts` treats this code as transient. Informational
   * only — the client's own retry loop decides purely from HTTP status
   * (`429`/`503`, §4.3), not from this flag, so this never causes a retry by
   * itself.
   */
  get retryable() {
    return getErrorCodeMeta(this.code).retryable;
  }
  /** Builds an `ApiError` from a parsed `{ errors, meta }` envelope (§4.3), using the first entry as the primary error. */
  static fromErrorEnvelope(envelope, status) {
    const [first, ...rest] = envelope.errors;
    return new _ApiError({
      status,
      code: first?.code ?? "UNKNOWN_ERROR",
      message: first?.message ?? "An unknown error occurred",
      field: first?.field ?? null,
      requestId: envelope.meta?.requestId,
      errors: envelope.errors,
      ...rest.length > 0 ? { details: rest } : {}
    });
  }
  /** Builds an `ApiError` for a request that never reached the server (§4.3: distinct from a `5xx` the server actually returned). */
  static network(message, cause) {
    return new _ApiError({ status: 0, code: "NETWORK_ERROR", message, cause });
  }
};
var DEFAULT_LOCALE = "en-IN";
var IDEMPOTENCY_HEADER = "X-Idempotency-Key";
function buildRequestHeaders(options) {
  const {
    authStore,
    context,
    appName,
    getLocale,
    extraHeaders,
    publicRoute,
    authDisabled,
    devIdentity,
    hasBody
  } = options;
  const headers = new Headers();
  if (hasBody) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("X-Correlation-ID", `${context}:${generateCorrelationId()}`);
  headers.set("Accept-Language", getLocale?.() ?? DEFAULT_LOCALE);
  if (publicRoute || !authStore) {
    return applyExtraHeaders(headers, extraHeaders);
  }
  const state = authStore.getState();
  if (state.accessToken && !authDisabled) {
    headers.set("Authorization", `Bearer ${state.accessToken}`);
  }
  const tenantId = state.user?.tenantId ?? state.tenantConfig?.tenantId ?? devIdentity?.tenantId;
  if (tenantId) {
    headers.set("X-Tenant-ID", tenantId);
  }
  if (authDisabled && devIdentity) {
    headers.set("X-Dev-User-Id", devIdentity.userId);
    headers.set("X-Dev-Roles", devIdentity.roles.join(","));
    if (devIdentity.customerId) {
      headers.set("X-Dev-Customer-Id", devIdentity.customerId);
    }
    if (devIdentity.kycTier) {
      headers.set("X-Dev-Kyc-Tier", devIdentity.kycTier);
    }
  }
  if (state.user?.sessionId) {
    headers.set("X-Device-Session-Id", state.user.sessionId);
  }
  if (appName) {
    headers.set("X-App-Name", appName);
  }
  return applyExtraHeaders(headers, extraHeaders);
}
function applyExtraHeaders(headers, extraHeaders) {
  if (extraHeaders) {
    for (const [key, value] of Object.entries(extraHeaders)) {
      headers.set(key, value);
    }
  }
  return headers;
}

// src/interceptors/requestDeduplication.ts
function buildDedupeKey(input) {
  const sortedParams = input.params ? Object.fromEntries(Object.entries(input.params).sort(([a], [b]) => a.localeCompare(b))) : {};
  return `${input.method}:${input.url}:${JSON.stringify(sortedParams)}`;
}
var RequestDeduplicator = class {
  inFlight = /* @__PURE__ */ new Map();
  /** Returns the already-in-flight promise for `key` if one exists; otherwise starts one via `factory` and tracks it until it settles. */
  dedupe(key, factory) {
    const existing = this.inFlight.get(key);
    if (existing) {
      return existing;
    }
    const promise = factory().finally(() => {
      this.inFlight.delete(key);
    });
    this.inFlight.set(key, promise);
    return promise;
  }
};
var RETRYABLE_STATUSES = /* @__PURE__ */ new Set([429, 503]);
var DEFAULT_MAX_RETRIES = 2;
function shouldRetry(input) {
  if (!RETRYABLE_STATUSES.has(input.status)) return false;
  if (input.attempt >= input.maxRetries) return false;
  return input.method === "GET" || Boolean(input.explicitlyRetryable);
}
function delayForRetry(attempt) {
  const delay2 = computeBackoffDelay(attempt);
  return new Promise((resolve) => setTimeout(resolve, delay2));
}

// src/factory/createApiClient.ts
var IDEMPOTENT_IN_PROGRESS = "IDEMPOTENT_REQUEST_IN_PROGRESS_409_003";
function buildUrl(baseUrl, path, params) {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path.replace(/^\/+/, "");
  const queryIndex = trimmedPath.indexOf("?");
  const pathOnly = queryIndex === -1 ? trimmedPath : trimmedPath.slice(0, queryIndex);
  const inlineQuery = queryIndex === -1 ? "" : trimmedPath.slice(queryIndex + 1);
  const searchParams = new URLSearchParams(inlineQuery);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === void 0 || value === null) continue;
      searchParams.set(key, String(value));
    }
  }
  const queryString = searchParams.toString();
  return `${trimmedBase}/${pathOnly}${queryString ? `?${queryString}` : ""}`;
}
async function safeParseJson(response) {
  try {
    return await response.json();
  } catch {
    return void 0;
  }
}
async function toApiError(response) {
  const body = await safeParseJson(response);
  if (isErrorEnvelope(body)) {
    return ApiError.fromErrorEnvelope(body, response.status);
  }
  return new ApiError({
    status: response.status,
    code: "UNKNOWN_ERROR",
    message: response.statusText || `Request failed with status ${response.status}`
  });
}
function isDevelopmentBuild() {
  return typeof process !== "undefined" && typeof process.env !== "undefined" && process.env["NODE_ENV"] !== "production";
}
function createApiClient(config) {
  const {
    baseUrl,
    context,
    appName,
    authStore,
    onAuthFailure,
    getLocale,
    fetchImpl = fetch,
    maxRetries = DEFAULT_MAX_RETRIES,
    onResponseMeta,
    authDisabled = false,
    devIdentity,
    refreshCoordinator = sharedRefreshCoordinator
  } = config;
  const deduplicator = new RequestDeduplicator();
  const warnedGeneratedKeys = /* @__PURE__ */ new Set();
  function resolveAppName() {
    return typeof appName === "function" ? appName() : appName;
  }
  function applyIdempotencyHeader(headers, method, args) {
    if (method === "GET" || args.skipIdempotencyKey) return;
    if (args.idempotencyKey) {
      headers.set(IDEMPOTENCY_HEADER, args.idempotencyKey);
      return;
    }
    if (args.allowGeneratedKey) {
      headers.set(IDEMPOTENCY_HEADER, generateCorrelationId());
      return;
    }
    if (isDevelopmentBuild()) {
      const signature = `${method} ${args.url}`;
      if (!warnedGeneratedKeys.has(signature)) {
        warnedGeneratedKeys.add(signature);
        console.warn(
          `[api-client] ${signature} is a mutation with no idempotencyKey. Supply a caller-stable key (e.g. "enroll:{schemeId}:{draftId}") so a double submit cannot become two writes, or set skipIdempotencyKey for a non-@IdempotentRequest endpoint \u2014 backend-api-integration-design.md I9.`
        );
      }
    }
  }
  async function performRequest(args, attempt, hasRefreshed, idempotencyRetried) {
    const method = args.method ?? "GET";
    const url = buildUrl(baseUrl, args.url, args.params);
    const resolvedAppName = resolveAppName();
    const headers = buildRequestHeaders({
      authStore,
      context: String(context),
      ...resolvedAppName ? { appName: resolvedAppName } : {},
      ...getLocale ? { getLocale } : {},
      ...args.headers ? { extraHeaders: args.headers } : {},
      ...args.publicRoute ? { publicRoute: true } : {},
      ...authDisabled ? { authDisabled: true } : {},
      ...devIdentity ? { devIdentity } : {},
      hasBody: args.body !== void 0
    });
    applyIdempotencyHeader(headers, method, args);
    let response;
    try {
      response = await fetchImpl(url, {
        method,
        headers,
        // §6.2: the API surface is authenticated purely by the Authorization
        // header under D1. The only cookie-authenticated call in the platform is
        // POST /auth/refresh, which `@quickbee/auth` issues itself with
        // credentials: 'include'. Sending credentials here would widen the CORS
        // requirement for no benefit.
        credentials: "omit",
        ...args.signal ? { signal: args.signal } : {},
        ...args.body !== void 0 ? { body: JSON.stringify(args.body) } : {}
      });
    } catch (cause) {
      throw ApiError.network("Network request failed", cause);
    }
    if (response.status === 401) {
      if (hasRefreshed || authDisabled || args.publicRoute || !authStore) {
        onAuthFailure?.();
        throw await toApiError(response);
      }
      const refreshed = await refreshCoordinator.attemptRefresh({
        authStore,
        onAuthFailure: onAuthFailure ?? (() => {
        }),
        ...config.fetchImpl ? { fetchImpl: config.fetchImpl } : {}
      });
      if (refreshed) {
        return performRequest(args, attempt, true, idempotencyRetried);
      }
      throw await toApiError(response);
    }
    if (shouldRetry({
      status: response.status,
      method,
      attempt,
      maxRetries,
      ...args.retryable !== void 0 ? { explicitlyRetryable: args.retryable } : {}
    })) {
      await delayForRetry(attempt);
      return performRequest(args, attempt + 1, hasRefreshed, idempotencyRetried);
    }
    if (response.status === 409 && !idempotencyRetried) {
      const error = await toApiError(response);
      if (error.code === IDEMPOTENT_IN_PROGRESS) {
        await delayForRetry(attempt);
        return performRequest(args, attempt, hasRefreshed, true);
      }
      throw error;
    }
    if (!response.ok) {
      throw await toApiError(response);
    }
    if (response.status === 204) {
      return void 0;
    }
    if (args.responseType === "binary") {
      return await toFileDownload(response, url);
    }
    const body = await safeParseJson(response);
    if (onResponseMeta) {
      const meta = readResponseMeta(body);
      if (meta) onResponseMeta(meta, { url, method, status: response.status });
    }
    return unwrapEnvelope(body);
  }
  function request(args) {
    const method = args.method ?? "GET";
    if (method === "GET") {
      const key = buildDedupeKey({
        method,
        url: args.url,
        ...args.params ? { params: args.params } : {}
      });
      return deduplicator.dedupe(key, () => performRequest(args, 0, false, false));
    }
    return performRequest(args, 0, false, false);
  }
  return {
    request,
    get: (url, options) => request({ ...options, url, method: "GET" }),
    post: (url, body, options) => request({ ...options, url, method: "POST", body }),
    put: (url, body, options) => request({ ...options, url, method: "PUT", body }),
    patch: (url, body, options) => request({ ...options, url, method: "PATCH", body }),
    delete: (url, options) => request({ ...options, url, method: "DELETE" })
  };
}

// src/transport/mockClient.ts
function mockPage(items, pagination = {}) {
  return {
    items,
    pagination: {
      cursor: pagination.cursor ?? null,
      hasMore: pagination.hasMore ?? false,
      total: pagination.total ?? items.length
    }
  };
}
function mockPaginate(all, query) {
  const size = Math.min(Math.max(Number(query["size"] ?? 20) || 20, 1), 100);
  const offset = Number(query["cursor"] ?? 0) || 0;
  const items = all.slice(offset, offset + size);
  const nextOffset = offset + items.length;
  return {
    items,
    pagination: {
      cursor: nextOffset < all.length ? String(nextOffset) : null,
      hasMore: nextOffset < all.length,
      total: all.length
    }
  };
}
function mockError(status, code, message, field) {
  throw new ApiError({ status, code, message, field: field ?? null });
}
function compileRoutes(routes) {
  const compiled = Object.entries(routes).map(([key, handler]) => {
    const spaceIndex = key.indexOf(" ");
    if (spaceIndex === -1) {
      throw new Error(`[api-client] Mock route "${key}" must be "METHOD /path".`);
    }
    return {
      key,
      method: key.slice(0, spaceIndex).toUpperCase(),
      segments: key.slice(spaceIndex + 1).replace(/^\/+/, "").split("/"),
      handler
    };
  });
  return compiled.sort((a, b) => countParams(a.segments) - countParams(b.segments));
}
function countParams(segments) {
  return segments.filter((segment) => segment.startsWith(":")).length;
}
function matchRoute(compiled, method, path) {
  const requestSegments = path.replace(/^\/+/, "").split("/");
  for (const route of compiled) {
    if (route.method !== method || route.segments.length !== requestSegments.length) continue;
    const params = {};
    let matched = true;
    for (let i = 0; i < route.segments.length; i += 1) {
      const pattern = route.segments[i];
      const actual = requestSegments[i];
      if (pattern.startsWith(":")) {
        params[pattern.slice(1)] = decodeURIComponent(actual);
      } else if (pattern !== actual) {
        matched = false;
        break;
      }
    }
    if (matched) return { route, params };
  }
  return null;
}
function delay(ms) {
  return ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
}
function createMockClient(config) {
  const { routes, context, latencyMs = 120 } = config;
  let compiled = null;
  let loading = null;
  async function getRoutes() {
    if (compiled) return compiled;
    if (!loading) {
      loading = Promise.resolve(typeof routes === "function" ? routes() : routes).then((resolved) => {
        compiled = compileRoutes(resolved);
        return compiled;
      });
    }
    return loading;
  }
  async function request(args) {
    const method = args.method ?? "GET";
    const [pathOnly] = args.url.split("?");
    const table = await getRoutes();
    const match = matchRoute(table, method, pathOnly ?? "");
    if (!match) {
      throw new ApiError({
        status: 404,
        code: "NOT_FOUND_404_001",
        message: `[mock:${context}] No fixture for "${method} ${pathOnly}". Add it to the context package's mocks route table, or run against a real backend (QB_API_URL).`
      });
    }
    await delay(latencyMs);
    return await match.route.handler({
      params: match.params,
      query: args.params ?? {},
      body: args.body,
      method,
      url: args.url
    });
  }
  return {
    request,
    get: (url, options) => request({ ...options, url, method: "GET" }),
    post: (url, body, options) => request({ ...options, url, method: "POST", body }),
    put: (url, body, options) => request({ ...options, url, method: "PUT", body }),
    patch: (url, body, options) => request({ ...options, url, method: "PATCH", body }),
    delete: (url, options) => request({ ...options, url, method: "DELETE" })
  };
}

// src/factory/createContextClient.ts
function defaultLocale() {
  if (typeof document !== "undefined" && document.documentElement.lang) {
    return document.documentElement.lang;
  }
  return "en-IN";
}
var warnedUnreachable = /* @__PURE__ */ new Set();
function isDevelopmentBuild2() {
  return typeof process !== "undefined" && typeof process.env !== "undefined" && process.env["NODE_ENV"] !== "production";
}
function createContextClient(config) {
  const { context, mocks, publicClient } = config;
  const descriptor = getContextDescriptor(context);
  if (!descriptor.reachable && isDevelopmentBuild2() && !warnedUnreachable.has(context)) {
    warnedUnreachable.add(context);
    console.warn(
      `[api-client] Bounded context "${context}" has no deployed gateway stack. Screens backed by it work against fixtures only; any live request will fail explicitly (\xA716 B5).`
    );
  }
  let live = null;
  let mock = null;
  function liveClient() {
    if (live) return live;
    const baseUrl = resolveContextBaseUrl(context);
    const devSession = getDevSession();
    live = createApiClient({
      baseUrl,
      context,
      appName: config.appName ?? (() => getActiveApp()),
      authStore: publicClient ? null : config.authStore ?? useAuthStore,
      getLocale: config.getLocale ?? defaultLocale,
      ...config.onAuthFailure ? { onAuthFailure: config.onAuthFailure } : {},
      ...config.fetchImpl ? { fetchImpl: config.fetchImpl } : {},
      ...config.maxRetries !== void 0 ? { maxRetries: config.maxRetries } : {},
      ...config.onResponseMeta ? { onResponseMeta: config.onResponseMeta } : {},
      ...isAuthDisabled() ? { authDisabled: true } : {},
      ...devSession ? { devIdentity: devSession } : {}
    });
    return live;
  }
  function mockClient() {
    if (mock) return mock;
    if (!mocks) {
      throw new Error(
        `[api-client] Context "${context}" is running in mock mode but ships no fixtures. Either add a mocks route table to @quickbee/api-${context}, or set QB_API_URL to run against a backend.`
      );
    }
    mock = createMockClient({
      routes: mocks,
      context,
      ...config.mockLatencyMs !== void 0 ? { latencyMs: config.mockLatencyMs } : {}
    });
    return mock;
  }
  function transport() {
    return getApiMode() === "mock" ? mockClient() : liveClient();
  }
  return {
    request: async (args) => transport().request(args),
    get: async (url, options) => transport().get(url, options),
    post: async (url, body, options) => transport().post(url, body, options),
    put: async (url, body, options) => transport().put(url, body, options),
    patch: async (url, body, options) => transport().patch(url, body, options),
    delete: async (url, options) => transport().delete(url, options)
  };
}
var clients = /* @__PURE__ */ new Map();
function getContextClient(config) {
  const key = `${config.context}${config.publicClient ? ":public" : ""}`;
  let client = clients.get(key);
  if (!client) {
    client = createContextClient(config);
    clients.set(key, client);
  }
  return client;
}
function resetContextClients() {
  clients.clear();
  warnedUnreachable.clear();
}
var UploadIncompleteError = class extends Error {
  /** True when the object reached S3 but the confirm leg never succeeded. */
  uploaded;
  constructor(message, uploaded, options) {
    super(message, options);
    this.name = "UploadIncompleteError";
    this.uploaded = uploaded;
  }
};
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function uploadToPresignedUrl(options) {
  const { ticket, file, confirm, maxConfirmAttempts = 3, signal, fetchImpl = fetch } = options;
  let response;
  try {
    response = await fetchImpl(ticket.uploadUrl, {
      method: "PUT",
      // Only the headers S3 handed back. No identity headers, no Content-Type
      // the signature did not cover.
      headers: ticket.headers ?? {},
      body: file,
      credentials: "omit",
      ...signal ? { signal } : {}
    });
  } catch (cause) {
    throw ApiError.network("Upload to storage failed", cause);
  }
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: "UPLOAD_FAILED",
      message: `Storage rejected the upload (${response.status}).`
    });
  }
  let lastError;
  for (let attempt = 0; attempt < maxConfirmAttempts; attempt += 1) {
    try {
      const confirmation = await confirm();
      return { confirmed: true, confirmation };
    } catch (error) {
      lastError = error;
      if (attempt < maxConfirmAttempts - 1) {
        await sleep(computeBackoffDelay(attempt));
      }
    }
  }
  throw new UploadIncompleteError(
    "The file reached storage but could not be confirmed. Retry the confirmation before assuming the attachment is lost.",
    true,
    { cause: lastError }
  );
}

// src/money/decimal.ts
function toDecimalString(value) {
  if (value === null || value === void 0) return null;
  if (typeof value === "string") return value.trim() === "" ? null : value;
  if (!Number.isFinite(value)) return null;
  return String(value);
}
function isUnpriced(value) {
  return value === null || value === void 0 || typeof value === "string" && value.trim() === "";
}
function compareDecimals(a, b) {
  const left = normaliseForCompare(toDecimalString(a) ?? "0");
  const right = normaliseForCompare(toDecimalString(b) ?? "0");
  if (isZero(left) && isZero(right)) return 0;
  if (left.negative !== right.negative) return left.negative ? -1 : 1;
  const magnitude = compareMagnitude(left, right);
  return left.negative ? -magnitude : magnitude;
}
function normaliseForCompare(value) {
  const trimmed = value.trim();
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed.replace(/^\+/, "");
  const [whole = "0", fraction = ""] = unsigned.split(".");
  return { negative, whole: whole.replace(/^0+(?=\d)/, ""), fraction: fraction.replace(/0+$/, "") };
}
function isZero(value) {
  return /^0*$/.test(value.whole) && value.fraction === "";
}
function compareMagnitude(a, b) {
  if (a.whole.length !== b.whole.length) return a.whole.length < b.whole.length ? -1 : 1;
  if (a.whole !== b.whole) return a.whole < b.whole ? -1 : 1;
  const length = Math.max(a.fraction.length, b.fraction.length);
  const left = a.fraction.padEnd(length, "0");
  const right = b.fraction.padEnd(length, "0");
  if (left === right) return 0;
  return left < right ? -1 : 1;
}
function isPositive(value) {
  if (isUnpriced(value)) return false;
  return compareDecimals(value, "0") > 0;
}

export { ApiError, IDEMPOTENCY_HEADER, UploadIncompleteError, compareDecimals, createApiClient, createContextClient, createMockClient, errorCodeMap, getContextClient, getErrorCodeMeta, isErrorEnvelope, isPagedEnvelope, isPagedResult, isPositive, isSuccessEnvelope, isUnpriced, mockError, mockPage, mockPaginate, normalisePagination, parseContentDispositionFileName, readResponseMeta, resetContextClients, saveFileDownload, toDecimalString, toFileDownload, unwrapEnvelope, uploadToPresignedUrl };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map